'use client';

import React, { useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { customRichTextTags } from '@/utils';
import BlogCtaBackgroundVideo from './cta-background-video';

// ⚠️ `dynamic` 은 컴포넌트가 **트리에 마운트되는 순간** 청크를 받는다 — `isOpen={false}` 로
// 항상 렌더해두면 클릭하지 않은 방문자도 하이드레이션 직후 두 청크(+ 완료 모달의 motion
// 런타임)를 받는다. 그래서 아래에서 `hasOpened` 로 마운트 자체를 미룬다
const NewsletterSubscribeModal = dynamic(
  () => import('./newsletter-subscribe-modal'),
  { ssr: false },
);
const NewsletterSubscribedModal = dynamic(
  () => import('@/components/newsletter-subscribed-modal'),
  { ssr: false },
);

/**
 * ⚠️ 밴드를 `w-screen`(=100vw) 으로 늘리지 말 것 — 100vw 는 스크롤바 폭을 포함해 레이아웃
 * 뷰포트보다 넓어지고, 그만큼 페이지에 가로 스크롤이 생긴다. 호출부가 이 섹션을 폭 래퍼
 * **밖에** 두고, 안쪽 폭만 `containerClassName` 으로 받는다.
 */
export default function NewsletterCtaSection({
  containerClassName,
}: {
  containerClassName: string;
}) {
  const t = useTranslations('Blog.landing');
  const tNewsletter = useTranslations('Blog.newsletter');
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);
  const [isSubscribedOpen, setIsSubscribedOpen] = useState(false);
  // 첫 클릭 전까지 마운트를 미루는 플래그(아래 dynamic 주석 참고). 한 번 true 가 되면
  // 되돌리지 않는다 — 이미 받아온 청크를 두고 언마운트/재마운트를 반복할 이유가 없다
  const [hasOpened, setHasOpened] = useState(false);

  const openSubscribe = useCallback(() => {
    setHasOpened(true);
    setIsSubscribeOpen(true);
  }, []);
  const closeSubscribe = useCallback(() => setIsSubscribeOpen(false), []);
  // 완료 모달의 자동 닫힘 타이머가 리렌더마다 초기화되지 않게 참조를 고정한다
  const closeSubscribed = useCallback(() => setIsSubscribedOpen(false), []);

  const handleSubscribed = useCallback(() => {
    setIsSubscribeOpen(false);
    setIsSubscribedOpen(true);
  }, []);

  return (
    <>
      <section className="relative w-full overflow-hidden py-[100px] lg:py-[150px]">
        {/* 배경 영상. 폴백 색을 래퍼에 둬(영상·poster 로드 실패 대비) -z-10 영상이
            섹션 배경에 가려지지 않게 한다(product/service-cta 선례) */}
        <div className="absolute inset-0 -z-10 bg-3b-blue-light-900">
          <BlogCtaBackgroundVideo className="h-full w-full object-cover" />
          {/* 영상은 어둡지만(평균 Y 45/255) 밝은 광선 줄기가 지나갈 때 흰 제목의 대비가
              떨어진다 — 같은 남색을 얇게 덮어 어느 프레임에서도 4.5:1 을 유지한다 */}
          <div className="absolute inset-0 bg-3b-blue-light-900/50" />
        </div>

        <div
          className={`${containerClassName} flex flex-col items-center gap-[46px] lg:flex-row lg:items-center lg:justify-between lg:gap-10`}
        >
          <div className="flex flex-col gap-2.5 text-center lg:text-left">
            <h2 className="break-keep text-h5 font-semibold text-3b-text-contrast lg:text-h4">
              {t.rich('newsletterCtaTitle', customRichTextTags)}
            </h2>
            <p className="break-keep text-sh1 font-medium text-3b-text-hint lg:text-h5 lg:font-semibold">
              {t.rich('newsletterCtaDescription', customRichTextTags)}
            </p>
          </div>

          <TrackedButton
            label="newsletter_subscribe_cta"
            position={TRACKED_EVENT_POSITION.MAIN_CTA}
            type="button"
            onClick={openSubscribe}
            // hover 색은 대응 토큰이 없어 리터럴이다 — 3b-red-light-600(#ff143d) 과
            // 다른 값이니 그 토큰으로 바꾸지 말 것
            className="shrink-0 rounded-[53px] bg-3b-background-surface px-10 py-[15px] text-sh2 font-bold text-3b-blue-light-900 transition-colors hover:bg-[#ff412d] hover:text-3b-text-contrast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-3b-background-surface focus-visible:ring-offset-2 focus-visible:ring-offset-3b-blue-light-900"
          >
            {tNewsletter('button')}
          </TrackedButton>
        </div>
      </section>

      {hasOpened ? (
        <>
          <NewsletterSubscribeModal
            isOpen={isSubscribeOpen}
            onClose={closeSubscribe}
            onSubscribed={handleSubscribed}
          />
          {/* 구독 모달 뒤에 둔다 — ModalWrapper 가 body 스크롤을 잠그므로, 구독 모달이
              닫히며 완료 모달이 열릴 때 잠금이 끊기지 않아야 한다 */}
          <NewsletterSubscribedModal
            isOpen={isSubscribedOpen}
            onClose={closeSubscribed}
          />
        </>
      ) : null}
    </>
  );
}
